import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuthStore } from '../store';
import { authService } from '../services';
import { useZodForm } from '../hooks';
import { loginSchema, LoginFormData } from '../schemas';
import { storage } from '../utils/storage';
import { getErrorMessage } from '../utils/errors';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';

// Brand logo/icon
const LogoIcon = () => (
  <svg className="w-10 h-10" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="40" height="40" rx="10" className="fill-primary-600" />
    <path
      d="M12 14h6v6h-6v-6zm10 0h6v3h-6v-3zm0 6h6v8h-6v-8zm-10 3h6v5h-6v-5z"
      className="fill-white"
    />
  </svg>
);

const Login: React.FC = () => {
  const navigate = useNavigate();
  const { setUser } = useAuthStore();
  const [rememberMe, setRememberMe] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    setValue,
    setError,
    formState: { errors, isSubmitting },
  } = useZodForm<LoginFormData>(loginSchema, {
    defaultValues: { email: '', password: '' },
    // Validate on submit so empty/invalid submits surface field errors instead of
    // the button sitting silently disabled (a dead-click trap).
    mode: 'onSubmit',
  });

  const fillDevCredentials = (email: string) => {
    setValue('email', email, { shouldValidate: true, shouldDirty: true });
    setValue('password', 'Test1234!', { shouldValidate: true, shouldDirty: true });
  };

  const onSubmit = async (data: LoginFormData) => {
    setFormError(null);
    try {
      const response = await authService.login(data);
      localStorage.setItem('auth-remember-me', String(rememberMe));
      storage.changeStorage(rememberMe ? localStorage : sessionStorage);
      setUser(response.user, response.accessToken, response.refreshToken);
      navigate('/dashboard');
    } catch (err: unknown) {
      // Surface the failure inline next to the form (not a corner toast that
      // vanishes) and highlight both credential fields without duplicating text.
      setFormError(getErrorMessage(err, 'Login failed. Please check your credentials and try again.'));
      setError('email', { type: 'manual' });
      setError('password', { type: 'manual' });
    }
  };

  // min-h-[125dvh] (= 100dvh ÷ the 0.8 body zoom) so the split panel fills the
  // viewport instead of rendering at 80% and leaving a dark gap at the bottom.
  return (
    <div className="min-h-[125dvh] flex bg-neutral-50">
      {/* Left side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-primary-600 to-primary-800 p-12 flex-col justify-between">
        <div>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-surface/20 rounded-xl flex items-center justify-center">
              <svg
                className="w-6 h-6 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M4 5a1 1 0 011-1h4a1 1 0 011 1v5a1 1 0 01-1 1H5a1 1 0 01-1-1V5zm10 0a1 1 0 011-1h4a1 1 0 011 1v2a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zm0 6a1 1 0 011-1h4a1 1 0 011 1v7a1 1 0 01-1 1h-4a1 1 0 01-1-1v-7zm-10 2a1 1 0 011-1h4a1 1 0 011 1v5a1 1 0 01-1 1H5a1 1 0 01-1-1v-5z"
                />
              </svg>
            </div>
            <span className="text-xl font-semibold text-white">Orchestrate</span>
          </div>
        </div>

        <div className="space-y-6">
          <h1 className="text-4xl font-bold text-white leading-tight">
            Streamline your
            <br />
            workflow management
          </h1>
          <p className="text-primary-100 text-lg max-w-md">
            Build, assign, and monitor workflows with a powerful visual interface. Keep your team
            aligned and productive.
          </p>
        </div>

        <div className="flex items-center gap-4 text-primary-200 text-sm">
          <span>Trusted by teams worldwide</span>
          <span className="w-1 h-1 rounded-full bg-primary-300" />
          <span>Enterprise ready</span>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden flex justify-center mb-8">
            <div className="flex items-center gap-3">
              <LogoIcon />
              <span className="text-xl font-semibold text-neutral-900">Orchestrate</span>
            </div>
          </div>

          <div className="text-center mb-8">
            <h2 className="text-2xl font-semibold text-neutral-900">Welcome back</h2>
            <p className="mt-2 text-sm text-neutral-500">Sign in to your account to continue</p>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <Input
              {...register('email')}
              label="Email"
              required
              type="email"
              placeholder="you@example.com"
              error={!!errors.email}
              errorMessage={errors.email?.message}
              autoComplete="email"
              autoFocus
            />

            <Input
              {...register('password')}
              label="Password"
              required
              type="password"
              placeholder="Enter your password"
              error={!!errors.password}
              errorMessage={errors.password?.message}
              autoComplete="current-password"
            />

            <div className="flex items-center justify-between text-sm">
              <label className="flex items-center gap-2 text-neutral-600">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={e => setRememberMe(e.target.checked)}
                  className="w-4 h-4 rounded border-neutral-300 text-primary-600"
                />
                Remember me
              </label>
            </div>

            {formError && (
              <div
                role="alert"
                className="rounded-lg border border-danger-200 bg-danger-50 px-3 py-2 text-sm text-danger-700"
              >
                {formError}
              </div>
            )}

            <Button
              type="submit"
              variant="primary"
              size="lg"
              fullWidth
              loading={isSubmitting}
              disabled={isSubmitting}
            >
              Sign in
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-neutral-500">
            Don't have an account?{' '}
            <Link to="/register" className="text-primary-600 hover:text-primary-700 font-medium">
              Create one
            </Link>
          </p>

          {/* Dev credentials hint — only in development */}
          {process.env.NODE_ENV === 'development' && (
            <div className="mt-6 border border-dashed border-neutral-300 rounded-lg p-4 bg-neutral-50">
              <p className="text-xs text-neutral-500 font-medium uppercase tracking-wide mb-3">
                Dev Accounts <span className="text-neutral-400 normal-case tracking-normal">(password: Test1234!)</span>
              </p>
              <div className="space-y-2">
                {[
                  { email: 'manager@mail.com', role: 'Owner' },
                  { email: 'worker1@mail.com', role: 'Worker' },
                  { email: 'worker2@mail.com', role: 'Worker' },
                ].map(({ email, role }) => (
                  <button
                    key={email}
                    type="button"
                    onClick={() => fillDevCredentials(email)}
                    className="w-full flex items-center justify-between px-3 py-2 rounded-md text-left text-sm hover:bg-neutral-100 transition-colors group"
                  >
                    <div>
                      <span className="text-neutral-700 group-hover:text-primary-600 font-medium">{email}</span>
                      <span className="ml-2 text-xs text-neutral-400">{role}</span>
                    </div>
                    <span className="text-xs text-neutral-400 group-hover:text-primary-500">Click to fill</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Login;
